import React, { useCallback, useMemo, useState } from 'react'
import {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
  UseFormMethods,
  UseFormOptions,
} from 'react-hook-form'
import { RegistrationError } from '../errors'
import { RegistrationFieldValues, RegistrationMessageData } from '../types'

export type UseRegistrationFormOptions<
  TFieldValues extends RegistrationFieldValues = RegistrationFieldValues
> = UseFormOptions<TFieldValues> & {
  submitDataValidationHandlers: ReadonlyArray<SubmitDataValidationHandler<TFieldValues>>
}

export type UseRegistrationForm<
  TFieldValues extends RegistrationFieldValues = RegistrationFieldValues
> = [
  registration: {
    messages: ReadonlyArray<RegistrationMessageData>
    addMessage: (message: RegistrationMessageData) => void
    dismissMessage: (message: RegistrationMessageData) => void
    dismissAllMessages: () => void
    patterns: { email: RegExp }
  },
  form: Omit<UseFormMethods<TFieldValues>, 'handleSubmit'> & {
    handleSubmit: <TSubmitFieldValues extends FieldValues = TFieldValues>(
      onValid: AsyncSubmitHandler<TSubmitFieldValues>,
      onInvalid?: SubmitErrorHandler<TFieldValues>
    ) => (e?: React.BaseSyntheticEvent) => Promise<void>
  }
]

type AsyncSubmitHandler<TFieldValues extends FieldValues> = (
  ...args: Parameters<SubmitHandler<TFieldValues>>
) => Promise<unknown>

export type SubmitDataValidationHandler<
  TFieldValues extends RegistrationFieldValues = RegistrationFieldValues
> = (data: UnpackNestedValue<TFieldValues>) => RegistrationMessageData[]

type RegistrationMessageMap = ReadonlyMap<string, RegistrationMessageData>

export const termsAndConditionsMessage: RegistrationMessageData = {
  type: 'error',
  name: 'termsAndConditions',
  details: 'You must agree to all terms and conditions.',
}

export const validateTerms: SubmitDataValidationHandler = (data) =>
  !data.termsandconditions ? [termsAndConditionsMessage] : []

export const validatePasswordsMatch: SubmitDataValidationHandler = (data) =>
  data.password !== data.password2
    ? [
        {
          type: 'error',
          name: 'passwords',
          details: 'Passwords do not match.',
        },
      ]
    : []

const addOrUpdateMessage =
  (message: RegistrationMessageData) =>
  (existingMap: RegistrationMessageMap): RegistrationMessageMap => {
    const existing = existingMap.get(message.name)
    if (existing && JSON.stringify(existing) === JSON.stringify(message)) {
      return existingMap
    }
    const updatedMap = new Map(existingMap)
    updatedMap.set(message.name, message)
    return updatedMap
  }

const removeMessage =
  ({ name }: Pick<RegistrationMessageData, 'name'>) =>
  (prevMap: RegistrationMessageMap): RegistrationMessageMap => {
    const existing = prevMap.get(name)
    if (!existing) {
      return prevMap
    }
    const updatedMap = new Map(prevMap)
    updatedMap.delete(name)
    return updatedMap
  }

const removeAllMessages = (prevMap: RegistrationMessageMap): RegistrationMessageMap => {
  return prevMap.size > 0 ? new Map() : prevMap
}

const sequenceValidationHandlers =
  <TFieldValues extends RegistrationFieldValues = RegistrationFieldValues>(
    handlers: ReadonlyArray<SubmitDataValidationHandler<TFieldValues>>
  ) =>
  (data: UnpackNestedValue<TFieldValues>): RegistrationMessageMap =>
    handlers
      .flatMap((handler) => handler(data))
      .reduce((acc: Map<string, RegistrationMessageData>, message) => {
        acc.set(message.name, message)
        return acc
      }, new Map())

export const useRegistrationForm = <
  TFieldValues extends RegistrationFieldValues = RegistrationFieldValues
>({
  submitDataValidationHandlers,
  defaultValues,
  ...formOptions
}: UseRegistrationFormOptions<TFieldValues>): UseRegistrationForm<TFieldValues> => {
  const {
    handleSubmit: handleSubmitForm,
    reset: resetForm,
    ...restMethods
  } = useForm<TFieldValues>(formOptions)

  const [messagesMap, setMessagesMap] = useState<RegistrationMessageMap>(new Map())

  const messages = useMemo(() => [...messagesMap.values()], [messagesMap])

  const addMessage = useCallback((message: RegistrationMessageData) => {
    setMessagesMap(addOrUpdateMessage(message))
  }, [])

  const dismissMessage = useCallback((message: RegistrationMessageData) => {
    setMessagesMap(removeMessage(message))
  }, [])

  const dismissAllMessages = useCallback(() => {
    setMessagesMap(removeAllMessages)
  }, [])

  const validateSubmitData = useMemo(
    () => sequenceValidationHandlers(submitDataValidationHandlers),
    [submitDataValidationHandlers]
  )

  const wrapOnValid = useCallback(
    <TSubmitFieldValues extends FieldValues = TFieldValues>(
      onValid: AsyncSubmitHandler<TSubmitFieldValues>
    ): AsyncSubmitHandler<TSubmitFieldValues> => {
      return async (data, event) => {
        const errorMessages = validateSubmitData(data as unknown as UnpackNestedValue<TFieldValues>)
        if (errorMessages.size) {
          setMessagesMap(errorMessages)
          return
        }
        setMessagesMap(removeAllMessages)
        try {
          const result = await onValid(data, event)
          if (!result) {
            return
          }
          addMessage({
            type: 'success',
            name: 'created',
            details: 'Your account was successfully created.',
          })
          resetForm()
          return result
        } catch (error: unknown) {
          const unexpectedMessage =
            'An error occurred while attempting sign up. Please try again later.'
          if (!error) {
            addMessage({ type: 'error', name: 'server', details: unexpectedMessage })
          } else if (error instanceof RegistrationError) {
            addMessage({
              type: 'error',
              name: 'server',
              details: error.message,
              actions: error.actions,
            })
          } else if (error instanceof Error) {
            addMessage({ type: 'error', name: 'server', details: error.message })
          } else if (typeof error === 'string') {
            addMessage({ type: 'error', name: 'server', details: error })
          } else {
            addMessage({ type: 'error', name: 'server', details: unexpectedMessage })
          }
        }
      }
    },
    [addMessage, resetForm, validateSubmitData]
  )

  const handleSubmit = useCallback(
    <TSubmitFieldValues extends FieldValues = TFieldValues>(
      onValid: AsyncSubmitHandler<TSubmitFieldValues>,
      onInvalid?: SubmitErrorHandler<TFieldValues>
    ) => {
      const onValidWrapper = wrapOnValid(onValid)
      return handleSubmitForm(onValidWrapper, onInvalid)
    },
    [handleSubmitForm, wrapOnValid]
  )

  const reset = useCallback(() => {
    dismissAllMessages()
    resetForm()
  }, [dismissAllMessages, resetForm])

  const patterns = useMemo(() => {
    return {
      email:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    }
  }, [])

  return [
    { messages, addMessage, dismissMessage, dismissAllMessages, patterns },
    { reset, handleSubmit, ...restMethods },
  ]
}
